import { useRef, useCallback, useMemo, useEffect } from 'react';
import debounce from 'lodash/debounce';
import {
  ESearchBarMode,
  ESearchBarStatus,
  getEffectiveSearchFilters,
  SEARCH_LIMIT,
  SearchBarFilters,
} from '../Context/types';
import SearchService from 'business/services/SearchService';
import { useSearchBarContext } from '../Context/SearchBarContext';

const SEARCH_DEBOUNCE_MS = 300;

export const useLiveSearch = () => {
  const {
    filters,
    mode,
    status,
    updateSearch,
    explorerStatus,
    explorerMode,
    updateExplorerSearch,
  } = useSearchBarContext();
  const liveRequestIdRef = useRef(0);
  const liveAbortControllerRef = useRef<AbortController | null>(null);
  const searchService = new SearchService();

  const handleLiveSearch = useCallback(
    async (searchFilters: SearchBarFilters) => {
      liveAbortControllerRef.current?.abort();
      const abortController = new AbortController();
      liveAbortControllerRef.current = abortController;
      const requestId = ++liveRequestIdRef.current;
      updateSearch(ESearchBarStatus.Loading);
      try {
        // Add 1 more than limit, to ensure there's enough search result to trigger showing the Show All Results button
        const limit = SEARCH_LIMIT + 1;
        const results = await searchService.SearchWithFilters(
          getEffectiveSearchFilters(searchFilters, ESearchBarMode.Basic),
          limit,
          abortController.signal,
        );
        if (requestId !== liveRequestIdRef.current) {
          return;
        }
        updateSearch(
          results.length > 0 ? ESearchBarStatus.Finished : ESearchBarStatus.NoResults,
          results,
        );
      } catch {
        if (requestId !== liveRequestIdRef.current) {
          return;
        }
        updateSearch(ESearchBarStatus.NoResults, []);
      }
    },
    [updateSearch],
  );

  const debouncedLiveSearch = useMemo(
    () => debounce((f: SearchBarFilters) => handleLiveSearch(f), SEARCH_DEBOUNCE_MS),
    [handleLiveSearch],
  );

  useEffect(() => () => debouncedLiveSearch.cancel(), [debouncedLiveSearch]);

  useEffect(() => {
    if (mode !== ESearchBarMode.Basic) {
      return;
    }
    const hasActiveCriteria = filters.query.trim() !== '';
    if (!hasActiveCriteria) {
      debouncedLiveSearch.cancel();
      liveRequestIdRef.current += 1;
      if (status !== ESearchBarStatus.Empty) {
        updateSearch(ESearchBarStatus.Empty, []);
      }
      if (
        explorerMode === ESearchBarMode.Basic &&
        explorerStatus !== ESearchBarStatus.Empty
      ) {
        updateExplorerSearch(ESearchBarStatus.Empty, [], mode);
      }
      return;
    }
    debouncedLiveSearch(filters);
  }, [filters.query, filters.targetTypes, mode]);
};
